<template>
  <page-layout ref="layout" @refresh="refresh">
    <template #breadcrumbs>
      <b-breadcrumb-item :text="`All-State - ${$store.state.settings.app.current.title}`" />
      <b-breadcrumb-item text="Scores" active/>
    </template>

    <template #actions="{ state }">
      <template v-if="isCreatedAtInCurrentYear(new Date().toISOString()) || $can('manage', 'all-state-scores')">
        <can do="update" on="all-state-scores">
          <b-button :disabled="!save.changes.length" variant="link" size="sm" @click="saveChanges(state)">
            <font-awesome-icon icon="fa-solid fa-cloud-arrow-up" class="mr-50"/> Update
          </b-button>
        </can>
      </template>
    </template>

    <template #dropdown-options>
      <b-dropdown-item @click="toggleLeftSidebar()">
        <feather-icon :icon="ui.layout.showLeftColumn ? 'Minimize2Icon' : 'Maximize2Icon'"/>
        <span class="align-middle ml-50">{{ ui.layout.showLeftColumn ? 'Hide' : 'Show' }} Sidebar</span>
      </b-dropdown-item>
      <b-dropdown-divider/>
      <b-dropdown-item :disabled="save.processing" @click="refresh">
        <feather-icon icon="RotateCwIcon"/>
        <span class="align-middle ml-50">Refresh</span>
      </b-dropdown-item>
    </template>

    <template #loading="{ state }">
      <overlay-loading :items="[
          { state: options.festivals.loading, desc: 'Loading Festivals'},
          { state: options.forms.loading, desc: 'Loading Forms'},
          { state: options.instruments.loading, desc: 'Loading Instruments'},
          { state: state.loading, desc: 'Rendering Template'},
          ]">
      </overlay-loading>
    </template>

    <template #content="{ state }">
      <validation-observer ref="table-observer">
        <b-alert v-if="save.processing || save.complete" show variant="primary" class="d-print-none">
          <b-list-group>
            <b-list-group-item>
              <div class="d-flex justify-content-between align-items-center">
                Processed Changes ({{ save.computedChanges.length }})
                <div class="d-flex align-items-center">
                  <b-button size="sm" variant="link"
                            :aria-expanded="save.details.processed ? 'true' : 'false'"
                            aria-controls="save-details-processed"
                            class="mr-1"
                            @click="save.details.processed = !save.details.processed">
                    {{ save.details.processed ? 'Hide' : 'Show' }} Details
                  </b-button>
                  <b-spinner v-if="save.processing" small :class="getSaveClass()"></b-spinner>
                  <b-icon v-else :icon="getSaveIcon(save)" :variant="getSaveVariant(save)" :class="getSaveClass()"/>
                </div>
              </div>
              <div v-if="save.processing" class="my-1">
                <small class="mb-25">{{ save.status }}</small>
                <b-progress>
                  <b-progress-bar :value="save.progress" :max="save.computedChanges.length"></b-progress-bar>
                </b-progress>
              </div>


              <!-- Element to collapse -->
              <vue-perfect-scrollbar ref="processed-scrollbar" :settings="{ maxScrollbarLength: 150,wheelPropagation: false }" class="ps-processed">
                <b-collapse id="save-details-processed" v-model="save.details.processed">
                  <b-list-group v-if="save.computedChanges.length" flush class="pt-50">
                    <b-list-group-item v-for="(item, index) in save.computedChanges" :key="index" class="px-0 py-25">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <small class="d-block">Application: {{ item.id }}</small>
                          <small v-for="(change, i2) in item.changes" :key="index + '-' + i2" class="d-block">
                            Update <span class="text-capitalize">{{ change.type }}</span> from {{ change.from }} to {{ change.to }}
                          </small>
                          <small class="d-block">
                            Updated <span class="text-capitalize">{{ item.updatedAt | date }}</span>
                          </small>
                          <small v-if="item.error" class="d-block text-danger">Error: {{ item.error }}</small>
                        </div>
                        <b-spinner v-if="item.saving" small></b-spinner>
                        <b-icon v-else :icon="item.icon" :variant="item.variant"/>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                  <small v-else class="pt-50">Nothings been updated yet.</small>
                </b-collapse>
              </vue-perfect-scrollbar>
            </b-list-group-item>
          </b-list-group>
          <div class="d-flex justify-content-end">
            <b-button v-if="save.complete" block class="mt-50 shadow-none" variant="outline-white" @click="save.complete = false">Back to Table</b-button>
          </div>
        </b-alert>

        <table-layout v-else-if="!save.processing && !save.complete"
            :loading="table.busy"
            :items="filteredApplications"
            :fields="table.fields"
            :filters="table.filters"
            :filters-options="table.filterOptions"
            :sorting="table.sorting"
            :search="table.search"
            :empty-text="table.emptyText"
            :export-exclude-fields="['*']"
            @search="(search) => table.search.value = search.value">

          <template #overlay>
            <overlay-loading
                :items="[
                { state: table.busy, desc: 'Loading Applications', loaded: table.paging.total },
              ]"/>
          </template>

          <template #filters>
            <b-form-group label="Festival">
              <v-select id="festival-input"
                        v-model="table.filters.festival.value" label="name"
                        :options="options.festivals.items"
                        :loading="options.festivals.loading"
                        :reduce="val => val.id"
                        :select-on-tab="true"
                        :filter="filterFestival"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        class="w-100">
                <template #option="{ id, name, site }">
                  <span class="d-block">Festival {{ name }} <small v-if="settings.showFilterCounts">({{ filterFestivalCount(id) }})</small></span>
                  <small>{{ site }}</small>
                </template>
                <template #selected-option="{ id, name, site }">
                  <span class="d-block">Festival {{ name }} - {{ site }}</span>
                </template>
              </v-select>
            </b-form-group>

            <b-form-group label="Instrument">
              <v-select id="instrument-input"
                        v-model="table.filters.instrument.value"
                        label="name"
                        :options="options.instruments.items"
                        :loading="options.instruments.loading"
                        :reduce="val => val.id"
                        :select-on-tab="true"
                        :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                        class="w-100">
                <template #option="{ id, name }">
                  {{ name }} <small v-if="settings.showFilterCounts">({{ filterInstrumentCount(id) }})</small>
                </template>
              </v-select>
            </b-form-group>
          </template>

          <template #sidebar>
            <b-card-actions action-collapse collapsed>
              <template #title>
                <b-card-title class="d-flex align-items-center justify-content-between w-100">
                  <div class="d-flex align-items-center">
                    <b-icon
                      icon="gear"
                      class="mr-50"
                      style="width: 16px; height: 16px"
                    />
                    <span class="align-middle">Options</span>
                  </div>
                </b-card-title>
              </template>

              <b-row class="pb-1">
                <b-col>
                  <label>Show Filter Counts:</label>
                </b-col>
                <b-col cols="auto">
                  <b-form-checkbox v-model="settings.showFilterCounts" switch @change="onShowFilterCounts"/>
                </b-col>
              </b-row>
              <b-row class="pb-1">
                <b-col>
                  <label>Hide Scored Applications:</label>
                </b-col>
                <b-col cols="auto">
                  <b-form-checkbox v-model="settings.hideScored" switch @change="toggleHideScored" />
                </b-col>
              </b-row>
              <b-row class="pb-1">
                <b-col>
                  <label>Score As Combo:</label>
                </b-col>
                <b-col cols="auto">
                  <b-form-checkbox v-model="settings.scoreAsCombo" switch @change="onScoreAsCombo"/>
                </b-col>
              </b-row>
            </b-card-actions>

            <b-alert show variant="primary" class="d-print-none">
              <b-list-group>
                <!-- Cognito User -->
                <b-list-group-item>
                  <div class="d-flex justify-content-between align-items-center">
                    Pending Changes ({{ updatableValues.length }})
                    <div class="d-flex align-items-center">
                      <b-button size="sm" variant="link"
                                :aria-expanded="save.details.changes ? 'true' : 'false'"
                                aria-controls="save-details-changes"
                                class="px-0"
                                @click="save.details.changes = !save.details.changes">
                        {{ save.details.changes ? 'Hide' : 'Show' }} Details
                      </b-button>
                    </div>
                  </div>
                  <!-- Element to collapse -->
                  <b-overlay :show="save.processing" variant="white" :opacity="1">
                    <vue-perfect-scrollbar :settings="{ maxScrollbarLength: 150,wheelPropagation: false }" class="ps-changes">
                      <b-collapse id="save-details-changes" v-model="save.details.changes">
                        <b-list-group v-if="updatableValues.length" flush class="pt-50">
                          <b-list-group-item v-for="(item, index) in updatableValues" :key="index" class="px-0 py-25">
                            <div class="d-flex justify-content-between align-items-center">
                              <div>
                                <small class="d-block">Application: {{ item.id }}</small>
                                <small v-for="(change, i2) in item.changes" :key="index + '-' + i2" class="d-block">
                                  Update <span class="text-capitalize">{{ change.type }}</span> from {{ change.from }} to {{ change.to }}
                                </small>
                              </div>
                              <b-button variant="link" size="sm" class="btn-icon" @click="table.search.value = item.id">
                                <b-icon-search />
                              </b-button>
                            </div>
                          </b-list-group-item>
                        </b-list-group>
                        <small v-else class="pt-50">Nothings been updated yet.</small>
                      </b-collapse>
                    </vue-perfect-scrollbar>
                  </b-overlay>
                </b-list-group-item>
              </b-list-group>
            </b-alert>
          </template>

          <template #row-details="data">
            <b-overlay :show="!data.item.hasDetails" opacity="1" @shown="getApplicationDetails(data.item)">
              <b-card no-body header-class="pb-0" class="mb-0 d-print-none">
                <template v-if="data.item.hasDetails">
                  <b-tabs card nav-wrapper-class="pt-1 pb-0 px-3" nav-class="flex-grow-1" active-tab-class="px-3">
                    <b-tab title="Details">
                      <application-details :current-user="user"
                                           :festival.sync="data.item.festival"
                                           :form.sync="data.item.form"
                                           :instrument.sync="data.item.instrument"
                                           :editable="data.item.editable" :show-heading="false"/>
                    </b-tab>
                    <b-tab title="Student">
                      <application-student :current-user="user"
                                           :student.sync="data.item.student"
                                           :create-student="false"
                                           :existing-students-options="{ show: false, clearable: false }"
                                           :editable="data.item.editable" :show-heading="false"/>
                    </b-tab>
                    <b-tab title="Questions">
                      <application-questions :form="data.item.form"
                                             :questions.sync="data.item.questions"
                                             :editable="data.item.editable" :show-heading="false"/>
                    </b-tab>
                    <b-tab title="Grading">
                      <application-grading :form="data.item.form"
                                           :student="data.item.student"
                                           :grading.sync="data.item.grading"
                                           :recommendation.sync="data.item.recommendation"
                                           :comments.sync="data.item.comments"
                                           :editable="data.item.editable" :show-heading="false"/>
                    </b-tab>

                    <template #tabs-end>
                      <b-nav-item :active="data.item.editable" class="ml-auto" role="presentation" @click.prevent="data.item.editable = !data.item.editable">
                        <font-awesome-icon icon="edit" class="text-primary"/>
                        <span>{{ data.item.editable ? 'Editing' : 'Edit' }}</span>
                      </b-nav-item>
                      <b-nav-item v-if="data.item.editable" role="presentation" @click.prevent="updateApplicationDetails(data.item)">
                        <font-awesome-icon icon="cloud-arrow-up" class="text-primary"/>
                        <span>Update</span>
                      </b-nav-item>
                    </template>
                  </b-tabs>
                </template>
                <template v-else>
                  <b-alert show variant="light">Loading</b-alert>
                </template>
              </b-card>
            </b-overlay>
          </template>

          <template #cell(student.name)="{data}">
            <b-media vertical-align="center" no-body>
              <b-media-aside>
                <b-avatar button variant="primary" size="2.5em"
                          badge-variant="primary" @click="data.toggleDetails">
                  <font-awesome-icon icon="fas fa-graduation-cap"></font-awesome-icon>
                  <template #badge>
                    <b-icon :icon="data.item._showDetails === true ? 'chevron-up' : 'chevron-down'" />
                  </template>
                </b-avatar>
              </b-media-aside>
              <b-media-body class="align-self-center">
                <h6 class="mb-0">{{ getPersonName(data.item.student, { format: 'lastFirst' }) }}</h6>
                <small v-if="data.item.student.school && data.item.student.school.name" class="text-wrap-balance">{{ data.item.student.school.name.legal }}</small>
                <small v-else class="text-danger">No School</small>
              </b-media-body>
            </b-media>
          </template>

          <template #cell(festival)="{data}">
            <div v-if="data.item.applicationFestivalId">
              {{ getFestivalName(data.item.applicationFestivalId) }}
            </div>
            <div v-else class="text-danger">No Festival</div>
          </template>

          <template #cell(instrument)="{data}">
            <div v-if="data.item.applicationInstrumentId">
              {{ getInstrumentName(data.item.applicationInstrumentId) }}
            </div>
            <div v-else class="text-danger">No Instrument</div>
          </template>

          <template #cell(ranking.local)="{data}">
            <validation-provider v-slot="validationContext" :ref="`${data.item.id}-local-provider`" :vid="`${data.item.id}-local-rank-provider`" name="Local Rank" :rules="rules.local">
              <b-input-group :class="getInputGroupClass(validationContext)">
                <b-form-input :id="`${data.item.id}-local-input`" :ref="`${data.item.id}-local-input`"
                              v-model="data.item.ranking.local"
                              number type="number"
                              placeholder="Local Rank"
                              :debounce="250"
                              :disabled="!$can('update', 'all-state-scores', 'local-rank')"
                              :tabindex="data.item.tabIndex.local"
                              :state="getValidationState(validationContext)"
                              @update="onChange('local', $event, rules.local, data.item)"
                              @focus="onFocus"
                              @blur="onBlur('local', data.item)"/>
                <b-input-group-append v-if="getValidationState(validationContext) !== null">
                  <b-input-group-text>
                    <font-awesome-icon icon="fa-solid fa-rotate-left" @click="undoToInitialItem('local', data.item)"/>
                  </b-input-group-text>
                </b-input-group-append>
              </b-input-group>
            </validation-provider>
          </template>

          <template #cell(ranking.score)="{data}">
            <validation-provider v-slot="validationContext" :ref="`${data.item.id}-score-provider`" :vid="`${data.item.id}-score-provider`" name="Score" :rules="rules.score">
              <b-form-group v-if="settings.scoreAsCombo" :state="getValidationState(validationContext)" class="mb-0">
                <b-input-group :class="getInputGroupClass(validationContext)">
                  <v-select :id="`${data.item.id}-score-select`" :ref="`${data.item.id}-score-select`"
                            v-model.number="data.item.ranking.score"
                            :options="options.scores"
                            taggable
                            :select-on-tab="true"
                            placeholder="0"
                            :tabindex="data.item.tabIndex.score"
                            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                            :disabled="!$can('update', 'all-state-scores', 'score')"
                            append-to-body :calculate-position="withPopper"
                            @input="onChange('score', $event, rules.score, data.item)"
                            @search:blur="onBlur('score', data.item)">
                    <!--append-to-body :calculate-position="withPopper"-->
                  </v-select>
                  <b-input-group-append v-if="getValidationState(validationContext) !== null" >
                    <b-input-group-text>
                      <font-awesome-icon icon="fa-solid fa-rotate-left" @click="undoToInitialItem('score', data.item)"/>
                    </b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <!-- append-to-body :calculate-position="withPopper"-->
              <b-input-group v-else :class="getInputGroupClass(validationContext)">
                <b-input
                    v-model="data.item.ranking.score" number
                    type="number" placeholder="Score"
                    :debounce="250"
                    :formatter="formatInput"
                    :tabindex="data.item.tabIndex.score"
                    :disabled="!$can('update', 'all-state-scores', 'score')"
                    :state="getValidationState(validationContext)"
                    @update="onChange('score', $event, rules.score, data.item)"
                    @blur="onBlur('score', data.item)"/>
                <b-input-group-append v-if="getValidationState(validationContext) !== null">
                  <b-input-group-text>
                    <font-awesome-icon icon="fa-solid fa-rotate-left" @click="undoToInitialItem('score', data.item)"/>
                  </b-input-group-text>
                </b-input-group-append>
              </b-input-group>
            </validation-provider>
          </template>

          <template #cell(createdAt)="{data}">
            <last-modified :date="data.item.createdAt" no-text />
          </template>

          <!-- Column: State - Enabled -->
          <template #cell(updatedAt)="{data}">
            <last-modified :date="data.item.updatedAt" no-text />
          </template>

          <template #cell(row-options)="{data}">
            <b-dropdown-item :to="{ name: 'all-state-application', params: { id: data.item.id } }"
                             class="table-row-option-view">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">View Application</span>
            </b-dropdown-item>
            <b-dropdown-item :to="{ name: 'all-state-student', params: { id: data.item.student.id } }"
                             class="table-row-option-view">
              <feather-icon icon="FileTextIcon" />
              <span class="align-middle ml-50">View Student</span>
            </b-dropdown-item>
            <b-dropdown-divider/>
            <b-dropdown-item-button class="table-row-option-details" @click="data.toggleDetails">
              <feather-icon :icon="`Toggle${data.item._showDetails ? 'Left': 'Right'}Icon`"/>
              <span class="align-middle ml-50">Toggle Row Details</span>
            </b-dropdown-item-button>
          </template>

        </table-layout>
      </validation-observer>
    </template>

    <template #debug>
      <b-row>
        <b-col>
          <debug title="Table Items" :collapsed="true">
            {{ table.items.slice(0, 10) }}
          </debug>
        </b-col>
        <b-col>
          <debug title="Table Initial Items Values" :collapsed="true">
            {{ table.initialItems.slice(0, 10) }}
          </debug>
        </b-col>
      </b-row>
    </template>

  </page-layout>
</template>

<script>
import _ from 'lodash'
import {validate} from 'vee-validate';
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import PageLayout from '@/components/PageLayout.vue';
import {API, Auth, graphqlOperation} from 'aws-amplify';
import {
  getApplication,
  getApplicationDetails,
  listApplications,
  listFestivals,
  listForms,
  listInstruments,
  listSchools,
  onCreateApplication,
  onUpdateApplication,
  updateApplication
} from './queries/scores-table';
import Fuse from 'fuse.js';
import print from '@/mixins/print.mixin';
import notify from '@/mixins/notify.mixin';
import BCardActions from '@core/components/b-card-actions/BCardActions.vue';
import OverlayLoadingText from '@/components/OverlayLoadingText.vue';
import OverlayLoading from '@/components/OverlayLoading.vue';
import cache from '@/mixins/storage.cache.mixin'
import local from '@/mixins/storage.local.mixin'
import TableRowOptions from '@/components/TableRowOptions.vue';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import ApplicationStudent from '@/views/all-state/application/ApplicationStudent.vue';
import ApplicationDetails from '@/views/all-state/application/ApplicationDetails.vue';
import ApplicationQuestions from '@/views/all-state/application/ApplicationQuestions.vue';
import ApplicationGrading from '@/views/all-state/application/ApplicationGrading.vue';
import {getUser} from '@/views/all-state/selections/selection-table';
import popper from '@/mixins/popper.mixin';
import settingsMixin from '@/mixins/settings.mixin';
import LastModified from '@/components/LastModified.vue';
import ScoresSorting from '@/views/all-state/scores/ScoresSorting.vue';
import TableLayout from '@/components/TableLayout.vue';
import personMixin from '@/mixins/person.mixin';

export default {
  directives: {
    Ripple,
  },
  filters: {
    date(value) {
      return value ? new Intl.DateTimeFormat('en', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }).format(new Date(value)) : null
    }
  },
  components: {
    TableLayout,
    ScoresSorting,
    LastModified,
    ApplicationGrading,
    ApplicationQuestions,
    ApplicationDetails,
    ApplicationStudent,
    TableRowOptions,
    OverlayLoading,
    OverlayLoadingText,
    BCardActions,
    PageLayout,
    vSelect,
    VuePerfectScrollbar
  },
  mixins: [ print, notify, cache, local, popper, settingsMixin, personMixin ],
  data() {
    return {
      user: null,
      settings: {
        hideScored: false,
        scoreAsCombo: true,
        showFilterCounts: false,
      },
      table: {
        busy: true,
        items: [],
        initialItems: [],
        fields: [
          {
            key: 'student.name',
            subKeys: ['student.name.last', 'student.name.first'],
            label: 'Student',
            sortable: false,
            filterable: true,
            visible: true,
            tdClass: 'align-middle',
            thClass: 'border-0',
            class: 'col-student'
          },
          {
            key: 'festival',
            label: 'Festival',
            sortable: true,
            filterable: true,
            visible: true,
            tdClass: 'align-middle',
            thClass: 'border-0',
            class: 'col-ranking-local',
            //formatter: ({ item }) => this.getFestivalName(item.applicationFestivalId),
          },
          {
            key: 'instrument',
            label: 'Instrument',
            sortable: true,
            filterable: true,
            visible: true,
            tdClass: 'align-middle',
            thClass: 'border-0',
            class: 'col-ranking-local',
            //formatter: ({ item }) => this.getInstrumentName(item.applicationInstrumentId),
          },
          {
            key: 'createdAt',
            label: 'Created',
            sortable: false,
            filterable: false,
            visible: false,
            tdClass: 'align-middle',
            thClass: 'border-0',
          },
          {
            key: 'updatedAt',
            label: 'Last Modified',
            sortable: false,
            filterable: false,
            visible: false,
            tdClass: 'align-middle',
            thClass: 'border-0',
          },
          {
            key: 'ranking.local',
            label: 'Local Rank',
            sortable: false,
            filterable: true,
            visible: true,
            tdClass: 'align-middle',
            thClass: 'border-0',
            class: 'col-ranking-local'
          },
          {
            key: 'ranking.score',
            label: 'Score',
            sortable: false,
            filterable: true,
            visible: true,
            tdClass: 'align-middle',
            thClass: 'border-0',
            class: 'col-ranking-score'
          },
          {
            key: 'row-options',
            label: '',
            sortable: false,
            filterable: false,
            visible: true,
            tdClass: 'align-middle',
            thClass: 'border-0',
            class: 'col-row-action'
          },
        ],
        filters: {
          festival: { key: 'applicationFestivalId', value: null },
          form: { key: 'applicationFormId', value: null },
          instrument: { key: 'applicationInstrumentId', value: null },
          school: { key: 'school.id', value: null },
          student: { key: 'student.id', value: null }
        },
        filterOptions: { visible: true, collapsed: false, cols: 20 },
        paging: {
          page: 1,
          size: 25,
          total: 0,
          options: {
            sizes: [10, 25, 50, 100]
          }
        },
        search: {
          value: '',
          keys: ['id', 'student.school.name.legal']
        },
        sorting: {
          mode: 'plan',
          plan: [
            { key: 'student.name.last', dir: 'asc' },
            { key: 'student.name.first', dir: 'asc' },
            { key: 'ranking.local', dir: 'asc' },
            { key: 'ranking.score', dir: 'desc' },
          ],
          fields: [
            { key: 'student.name.last', text: 'Last Name', type: 'string' },
            { key: 'student.name.first', text: 'First Name', type: 'string' },
            { key: 'ranking.local', text: 'Local Rank', type: 'number' },
            { key: 'ranking.score', text: 'Score', type: 'number' },
            { key: 'applicationFestivalId', text: 'Festival', type: 'string' },
            { key: 'applicationInstrumentId', text: 'Instrument', type: 'string' },
            { key: 'updatedAt', text: 'Last Updated', type: 'date' },
            { key: 'createdAt', text: 'Submitted', type: 'date' },
          ],
          presets: [
            {
              key: 'localRankScore',
              label: 'Local Rank → Score',
              plan: [
                { key: 'ranking.local', dir: 'asc' },
                { key: 'ranking.score', dir: 'desc' },
              ]
            },
            {
              key: 'lastNameLocalRankScore',
              label: 'Last Name → First Name → Local Rank → Score',
              plan: [
                { key: 'student.name.last', dir: 'asc' },
                { key: 'student.name.first', dir: 'asc' },
                { key: 'ranking.local', dir: 'asc' },
                { key: 'ranking.score', dir: 'desc' },
              ]
            },
            {
              key: 'festivalInstrumentLocalRankScore',
              label: 'Festival → Instrument → Local Rank → Score',
              plan: [
                { key: 'applicationFestivalId', dir: 'asc' },
                { key: 'applicationInstrumentId', dir: 'asc' },
                { key: 'ranking.local', dir: 'asc' },
                { key: 'ranking.score', dir: 'desc' },
              ]
            }
          ]
        },
        subscriptions: {
          onCreate: null,
          onUpdate: null,
          onDelete: null
        },
        loading: true,
      },
      ui: {
        layout: {
          showLeftColumn: true,
        },
        dragSort: {
          from: null,
          over: null,
        },
      },
      options: {
        scores: [100, 99, 98, 97, 96, 95, 94, 93, 92, 91, 90],
        festivals: {
          loading: true,
          items: []
        },
        forms: {
          loading: true,
          items: []
        },
        instruments: {
          loading: true,
          items: []
        },
        schools: {
          loading: false,
          items: []
        },
        applications: {
          loading: true,
          loaded: 0
        },
      },
      rules: {
        local: { required: true, numeric: true, min_value: 0, max_value: 100 },
        score: { required: true, numeric: true, min_value: 0, max_value: 100 }
      },
      icon: 'fas fa-clipboard',
      save: {
        complete: false,
        processing: false,
        status: '',
        changes: [],
        computedChanges: [],
        errors: [],
        progress: 0,
        details: {
          changes: false,
          processed: true
        },
      }
    }
  },
  computed: {
    filteredApplications() {
      if(this.table.busy || this.table.items.length === 0) {
        return []
      }

      //filter: if hide scored setting is enabled, and the rankings have values greater than 0 then return false, otherwise true
      const applications = this.table.items.filter(item => !(this.settings.hideScored && item.ranking.score > 0 && item.ranking.local > 0))
      let localTabIndex = 1;
      let scoreTabIndex = 2;

      return applications.map((item, index) => {
        const newItem = {
          ...item,
          _showDetails: false,
          tabIndex: {
            local: localTabIndex,
            score: scoreTabIndex
          },
          state: {
            visible: !(this.settings.hideScored && item.ranking.score > 0 && item.ranking.local > 0)
          }
        };

        localTabIndex += 2;
        scoreTabIndex += 2;
        return newItem;
      })
    },
    updatableValues() {
      const values = []
      this.table.initialItems.forEach(item => {
        const changes = this.save.changes.filter(change => change.id === item.id)
        if(changes.length) {
          const input = {
            id: item.id,
            ranking: { local: null, score: null},
            changes: [],
          }

          const localChange = this.save.changes.find(change => change.id === item.id && change.key === 'local')
          if(localChange) {
            input.ranking.local = localChange.value
            input.changes.push({ type: 'local rank', from: item.ranking.local, to: localChange.value})
          }
          else { input.ranking.local = item.ranking.local }

          const scoreChange = this.save.changes.find(change => change.id === item.id && change.key === 'score')
          if(scoreChange) {
            input.ranking.score = scoreChange.value
            input.changes.push({ type: 'score', from: item.ranking.score, to: scoreChange.value})
          }
          else { input.ranking.score = item.ranking.score }

          /** Determine Date **/
          input.date = new Date(Math.max(...changes.map(change => new Date(change.date)))).toISOString();

          values.push(input)
        }
      })
      return values.sort((a, b) => b.date.localeCompare(a.date))
    },
  },
  async mounted() {
    this.loadLocalSettings();
    this.loadQueryValues()

    this.$refs.layout.state.loading = false
    await this.getCurrentUser()
    await Promise.all([
      this.listFestivals(),
      this.listForms(),
      this.listInstruments()
    ]);

    await this.getApplications()
  },
  beforeDestroy() {
    if(this.table.subscriptions?.onCreate) {
      this.table.subscriptions.onCreate.unsubscribe()
    }
    if(this.table.subscriptions?.onUpdate) {
      this.table.subscriptions.onUpdate.unsubscribe()
    }
    if(this.table.subscriptions?.onDelete) {
      this.table.subscriptions.onDelete.unsubscribe()
    }
  },
  methods: {
    toggleLeftSidebar() {
      this.ui.layout.showLeftColumn = !this.ui.layout.showLeftColumn
      this.table.filterOptions.visible = this.ui.layout.showLeftColumn
    },
    loadLocalSettings() {
      if(this.$store.getters['storageLocal/isRouteLocalEnabled']('all-state-scores')) {
        const item = this.$store.getters['storageLocal/getItem']('all-state-scores')
        if(item) {
          console.log('Loaded local settings for All State Scores', item)
          if(item?.filter) {
            if(item.filter?.festival !== null) { this.table.filters.festival.value = item.filters.festival }
            if(item.filter?.form !== null) { this.table.filters.form.value = item.filters.form }
            if(item.filter?.instrument !== null) { this.table.filters.instrument.value = item.filters.instrument }
          }

          if(item?.paging) {
            if(item.paging?.page !== null) { this.table.paging.page = item.paging.page }
            if(item.paging?.size !== null) { this.table.paging.size = item.paging.size }
          }

          if (item?.sorting?.plan) {
            this.table.sorting.plan = this.sanitizeSortPlan(item.sorting.plan)
          }

          if(item?.settings) {
            if(item.settings?.scoreAsCombo !== null) { this.settings.scoreAsCombo = item.settings.scoreAsCombo }
            if(item.settings?.showFilterCounts !== null) { this.settings.showFilterCounts = item.settings.showFilterCounts }
            if(item.settings?.hideScored !== null) {
              this.settings.hideScored = item.settings.hideScored
              this.toggleHideScored(this.settings.hideScored)
            }
          }
        }
      }
    },
    loadQueryValues() {
      if(this.$route.query.filters) {
        this.table.filter = JSON.parse(atob(this.$route.query.filters))
      }
    },
    async refresh() {
      this.save.changes = []
      this.save.computedChanges = []
      this.save.processing = false
      this.save.complete = false

      this.table.paging.total = 0
      this.table.busy = true

      this.options.festivals.loading = true


      await Promise.all([
        this.listFestivals(),
        this.listApplications()
      ]);
      this.table.busy = false
    },
    async getCurrentUser() {
      /** Get Current User from Store **/
      const cognitoUser = await Auth.currentAuthenticatedUser()

      /** Get User from AppSync **/
      const response = await API.graphql(graphqlOperation(getUser, { id: cognitoUser.attributes['custom:user_id'] }));
      this.user = response.data.getUser
      this.user.groups = cognitoUser.signInUserSession.accessToken.payload['cognito:groups']
    },
    async listForms() {
      const response = await API.graphql(graphqlOperation(listForms));
      this.options.forms.items = response.data.listForms.items.sort((a, b) => a.name.localeCompare(b.name));
      this.options.forms.loading = false
    },
    async listFestivals(nextToken, pagedFestivals) {
      this.options.festivals.loading = true
      const festivals = pagedFestivals || []

      const input = {
        limit: 100,
        nextToken: nextToken,
        filter: {
          createdAt: {
            between: [
              this.settingsStore.app.current.year.start,
              this.settingsStore.app.current.year.end
            ]
          }
        },
      }

      const response = await API.graphql(graphqlOperation(listFestivals, input));
      festivals.push(...response.data.listFestivals.items);

      if(response.data.listFestivals.nextToken) {
        await this.listFestivals(response.data.listFestivals.nextToken, festivals)
      }
      else {
        this.options.festivals.items = festivals.sort((a, b) => a.name.localeCompare(b.name, 'en', { numeric: true }))
        if(!this.options.festivals.items.some(item => item.id === this.table.filters.festival.value)) {
          this.table.filters.festival.value = null
        }
        this.options.festivals.loading = false
        this.options.festivals.loaded = true
      }
    },
    async listInstruments() {
      const response = await API.graphql(graphqlOperation(listInstruments));
      this.options.instruments.items = response.data.listInstruments.items.sort((a, b) => a.name.localeCompare(b.name));
      this.options.instruments.loading = false
    },
    async listSchools(nextToken, pagedSchools) {
      this.options.schools.loading = true
      const schools = pagedSchools || []
      const response = await API.graphql(graphqlOperation(listSchools, { limit: 500, nextToken: nextToken }));
      schools.push(...response.data.listSchools.items);

      if(response.data.listSchools.nextToken) {
        await this.listSchools(response.data.listSchools.nextToken, schools)
      }
      else {
        this.options.schools.items = schools
        this.options.schools.loading = false
        this.options.schools.loaded = true
      }
    },

    async getApplications() {
      this.table.busy = true
      if(this.$store.getters['storageLocal/isRouteDisabled']('all-state-scores') || this.$store.getters['all-state-scores/isExpired']) {
        await this.listApplications();
      }
      else {
        await this.listApplicationsFromStore()
      }
      this.table.busy = false
    },
    async listApplicationsFromStore() {
      this.table.items = this.$store.getters['all-state-scores/getItems']
      this.table.initialItems = JSON.parse(JSON.stringify(this.table.items.map(item => ({ id: item.id, ranking: item.ranking }) ))) //json parse/stringify created a deep copy
      if(!this.table.items.length) {
        await this.listApplications();
      }
    },
    async listApplications(nextToken, pagedApplications) {
      const applications = pagedApplications || []
      const input = {
        limit: 500,
        nextToken: nextToken,
        filter: {
          createdAt: {
            between: [
              this.settingsStore.app.current.year.start,
              this.settingsStore.app.current.year.end
            ]
          }
        },
      }
      const response = await API.graphql(graphqlOperation(listApplications, input));
      applications.push(...response.data.listApplications.items)
      applications.forEach(application => {
        if (application.student && application.student.name) {
          application.student.name.first = application.student.name.first ? application.student.name.first.trim() : '';
          application.student.name.last = application.student.name.last ? application.student.name.last.trim() : '';
          application.student.name.full = `${application.student.name.first} ${application.student.name.last}`;
        }
        application.hasDetails = false
        application.editable = false
      })

      this.table.paging.total = applications.length
      if(response.data.listApplications.nextToken) {
        await this.listApplications(response.data.listApplications.nextToken, applications)
      }
      else {
        this.table.items = applications/*.sort(this.compareApplications())*/
        this.table.initialItems = JSON.parse(JSON.stringify(this.table.items.map(item => ({ id: item.id, ranking: item.ranking }) ))) //json parse/stringify created a deep copy
        await this.$store.dispatch('all-state-scores/setItems', {
          key: 'all-state-scores',
          items: this.table.items
        })
      }
    },
    async getApplicationDetails(item) {
      if(item.hasDetails === false) {
        const response = await API.graphql(graphqlOperation(getApplicationDetails, { id: item.id } ));
        const application = response.data.getApplication
        if(application) {
          const updatedItem = {...item};
          updatedItem.student.dob = application?.student?.dob
          updatedItem.student.grade = application?.student?.grade
          updatedItem.student.address = application?.student?.address
          updatedItem.student.email = application.student?.email
          updatedItem.form = application?.form
          updatedItem.festival = application?.festival
          updatedItem.instrument = application?.instrument
          updatedItem.questions = application?.questions
          updatedItem.grading = application?.grading
          updatedItem.recommendation = application?.recommendation
          updatedItem.comments = application?.comments
          updatedItem.hasDetails = true

          // Find the index of the item in the array
          const index = this.table.items.findIndex(i => i.id === item.id);

          // Replace the old item with the updated item using Vue.set to ensure reactivity
          if (index !== -1) {
            this.$set(this.table.items, index, updatedItem);
          }
        }
        else {
          item.hasDetails = true
        }
      }
    },
    async updateApplicationDetails(item) {
      // Make a copy of item and update the properties on the copy
      const updatedItem = {...item};
      updatedItem.applicationFestivalId = item.festival.id;
      updatedItem.applicationFormId = item.form.id;
      updatedItem.applicationInstrumentId = item.instrument.id;
      updatedItem.editable = false;

      const input = {
        id: item.id,
        applicationFestivalId: updatedItem.festival.id,
        applicationFormId: updatedItem.form.id,
        applicationInstrumentId: updatedItem.instrument.id,
        questions: updatedItem.questions,
        grading: updatedItem.grading,
        recommendation: updatedItem.recommendation,
        comments: updatedItem.comments
      }
      try {
        await API.graphql(graphqlOperation(updateApplication, { input: input }));

        // Find the index of the item in the array
        const index = this.table.items.findIndex(i => i.id === item.id);

        // Replace the old item with the updated item using Vue.set to ensure reactivity
        if (index !== -1) {
          this.$set(this.table.items, index, updatedItem);
        }
      }
      catch(error) {
        console.error(error)
      }
    },

    onCreateApplication() {
      this.table.subscriptions.onCreate = API.graphql(graphqlOperation(onCreateApplication)).subscribe(async (sourceData) => {
        this.table.busy = true
        const createdApp = sourceData.value.data.onCreateApplication
        if (createdApp) {
          const response = await API.graphql(graphqlOperation(getApplication, {id: createdApp.id}));
          const application = response.data.getApplication
          application.student.name.full = `${application.student.name.first} ${application.student.name.last}`

          this.table.paging.total += 1
          this.table.items = [application, ...this.table.items];
        }
        this.table.busy = false
      });
    },
    onUpdateApplication() {
      this.table.subscriptions.onUpdate = API.graphql(graphqlOperation(onUpdateApplication)).subscribe((sourceData) => {
        const updatedApplication = sourceData.value.data.onUpdateApplication // { id, ranking.local, ranking.score }
        if (updatedApplication && this.table.items.map(item => item.id).includes(updatedApplication.id)) {
          const application = this.table.items.find(app => app.id === updatedApplication.id)
          if(application.ranking.local !== updatedApplication.ranking.local || application.ranking.score !== updatedApplication.ranking.score) {
            this.syncNotification()
            application.ranking.local = updatedApplication.ranking.local
            application.ranking.score = updatedApplication.ranking.score
            this.table.initialItems = JSON.parse(JSON.stringify(this.table.items.map(item => ({ id: item.id, ranking: item.ranking }) )))
          }
        }
      });
    },

    getComputedChanges() {
      const values = []
      this.table.initialItems.forEach(item => {
        const changes = this.save.changes.filter(change => change.id === item.id)
        if(changes.length) {
          const input = {
            id: item.id,
            ranking: { local: null, score: null},
            saving: false,
            success: null,
            error: null,
            changes: [],
            icon: 'circle',
            variant: 'primary'
          }

          const localChange = this.save.changes.find(change => change.id === item.id && change.key === 'local')
          if(localChange) {
            input.ranking.local = localChange.value
            input.changes.push({ type: 'local rank', from: item.ranking.local, to: localChange.value})
          }
          else { input.ranking.local = item.ranking.local }

          const scoreChange = this.save.changes.find(change => change.id === item.id && change.key === 'score')
          if(scoreChange) {
            input.ranking.score = scoreChange.value
            input.changes.push({ type: 'score', from: item.ranking.score, to: scoreChange.value})
          }
          else { input.ranking.score = item.ranking.score }

          values.push(input)
        }
      })
      return values
    },

    async saveChanges(state) {
      this.save.complete = false
      this.save.processing = true

      this.save.status = 'Computing Changes'
      this.save.computedChanges = this.getComputedChanges()

      this.save.status = 'Saving Changes'
      await this.save.computedChanges.reduce(async (referencePoint, change, index) => {
        try {
          change.saving = true
          await referencePoint;
          this.save.progress = index + 1
          await API.graphql(graphqlOperation(updateApplication, { input: { id: change.id, ranking: change.ranking} } ));
          change.success = true
          change.variant = 'success'
          change.icon = 'check-circle-fill'
          change.updatedAt = new Date().toISOString()
        }
        catch (e121) {
          change.success = false
          change.variant = 'danger'
          change.icon = 'x-circle-fill'
          change.error = e121
          this.save.errors.push({ application: change.id, error: e121})
        }
        finally {
          change.saving = false
        }
      }, Promise.resolve());


      // Update Initial Items, as well as revert back bad values in the table to their initial value
      this.save.status = 'Updating Initial Items'
      this.save.computedChanges.forEach(change => {
        const initialItem = this.table.initialItems.find(item => item.id === change.id)
        if(initialItem) { initialItem.ranking = change.ranking }

        const tableItem = this.table.items.find(item => item.id === change.id)
        if(tableItem) {
          tableItem.ranking = JSON.parse(JSON.stringify(change.ranking))
          tableItem.updatedAt = change?.updatedAt ? change?.updatedAt : tableItem.updatedAt
        }
      })

      if(this.$store.getters['storageLocal/isRouteCacheEnabled']('all-state-scores')) {
        await this.$store.dispatch('all-state-scores/setItems', {
          key: 'all-state-scores',
          items: this.table.items
        })
      }

      this.save.processing = false
      this.save.changes = []
      this.save.complete = true
    },

    onChange(key, value, rules, application) {
      validate(value, rules).then(result => {
        if (result.valid) {
          const initialItem = this.table.initialItems.find(item => item.id === application.id)
          if(Number.parseInt(initialItem?.ranking[key], 10) === Number.parseInt(value, 10)) {
            this.save.changes = this.save.changes.filter(change => !(change.id === application.id && change.key === key))
            this.$refs[`${application.id}-${key}-provider`].reset()
          }
          else {
            const changeValue = { id: application.id, key: key, value: Number.parseInt(value, 10), date: new Date().toISOString() }
            const index = this.save.changes.findIndex(change => change.id === application.id && change.key === key)
            if(index >= 0) {
              this.save.changes.splice(index, 1, changeValue)
            }
            else {
              this.save.changes.push(changeValue)
            }
          }
        }
        else {
          this.save.changes = this.save.changes.filter(change => !(change.id === application.id && change.key === key))
        }
      })
    },
    onBlur(key, item) {
      const initialItem = this.table.initialItems.find(ii => ii.id === item.id)
      if(initialItem && (Number.parseInt(initialItem?.ranking[key], 10) === Number.parseInt(item?.ranking[key], 10))) {
        this.$refs[`${item.id}-${key}-provider`].reset()
      }
    },
    onFocus(event) {
      /* const input = event.target;
       const len = input.value.length;
       input.focus();
       if (input.setSelectionRange) {
           input.setSelectionRange(len, len);
       }
       else if (input.createTextRange) {
           const range = input.createTextRange();
           range.collapse(true);
           range.moveEnd('character', len);
           range.moveStart('character', len);
           range.select();
       }*/
    },

    /** Save Util **/
    getInputGroupClass(validationContext) {
      const state = this.getValidationState(validationContext)
      if(state === null) return ''
      return state ? 'is-valid' : 'is-invalid'
    },
    undoToInitialItem(key, item) {
      const initialItem = this.table.initialItems.find(initial => initial.id === item.id)
      if(initialItem) {
        item.ranking[key] = initialItem.ranking[key]
        this.save.changes = this.save.changes.filter(change => !(change.id === item.id && change.key === key))
        this.$refs[`${item.id}-${key}-provider`].reset()
      }
    },
    formatInput(value) {
      if (value < 0) { return 0 }
      return value
    },
    getSaveIcon(save) {
      if(save.complete) {
        if(save.computedChanges.every(item => item.success)) return 'check-circle-fill'
        if(save.computedChanges.every(item => item.error)) return 'x-circle-fill'
        return 'slash-circle-fill'
      }
      return 'circle'
    },
    getSaveVariant(save) {
      if(save.complete) {
        if(save.computedChanges.every(item => item.success)) return 'success'
        if(save.computedChanges.every(item => item.error)) return 'danger'
        return 'warning'
      }
      return 'primary'
    },
    getSaveClass() {
      if(this.save.processing || this.save.complete) {
        if(this.$refs['processed-scrollbar']?.ps?.scrollbarYActive === true) {
          return 'mr-2'
        }
      }
      return ''
    },

    /** Filters **/
    filterFestivalCount(id) {
      return this.table.items.filter(app => app.applicationFestivalId === id)
          .filter(app => (this.table.filters.form.value ? app.applicationFormId === this.table.filters.form.value : true))
          .filter(app => (this.table.filters.instrument.value ? app.applicationInstrumentId === this.table.filters.instrument.value : true)).length
    },
    filterInstrumentCount(id) {
      return this.table.items.filter(app => app.applicationInstrumentId === id)
          .filter(app => (this.table.filters.festival.value ? app.applicationFestivalId === this.table.filters.festival.value : true))
          .filter(app => (this.table.filters.form.value ? app.applicationFormId === this.table.filters.form.value : true)).length
    },

    /** Searching **/
    filterFestival(options, search) {
      const fuse = new Fuse(options, {
        keys: ['name', 'site'],
        threshold: 0.3,
        shouldSort: true
      })
      return search.length ? fuse.search(search).map(({ item }) => item) : fuse.list
    },

    /** Validation **/
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },

    getFestival(id) {
      const festival = this.options.festivals.items.find(item => item.id === id)
      if(festival) return festival
      return { name: 'Unknown', site: 'Unknown'}
    },
    getFestivalName(id) {
      const festival = this.options.festivals.items.find(item => item.id === id)
      if(festival) return festival.name
      return 'Unknown Festival'
    },
    getInstrumentName(id) {
      const instrument = this.options.instruments.items.find(item => item.id === id)
      if(instrument) return instrument.name
      return 'Unknown Instrument'
    },
    getStudentDisplayName(student) {
      const first = ((student && student.name && student.name.first) || '').trim()
      const last = ((student && student.name && student.name.last) || '').trim()

      if (first && last) return `${last}, ${first}`
      return last || first || ''
    },


    /** Settings **/
    toggleHideScored(isChecked) {
      //this.table.items.forEach(item => { if(item.ranking.local > 0 && item.ranking.score > 0) { item.state.visible = !isChecked } })
      this.$store.dispatch('storageLocal/updateItem', {
        key: 'all-state-scores',
        subKey: 'settings.hideScored',
        value: isChecked
      })
    },
    onShowFilterCounts(isChecked) {
      this.settings.showFilterCounts = isChecked
      this.$store.dispatch('storageLocal/updateItem', {
        key: 'all-state-scores',
        subKey: 'settings.showFilterCounts',
        value: isChecked
      })
    },
    onScoreAsCombo(isChecked) {
      this.settings.scoreAsCombo = isChecked
      this.$store.dispatch('storageLocal/updateItem', {
        key: 'all-state-scores',
        subKey: 'settings.scoreAsCombo',
        value: isChecked
      })
    },

    /** Paging **/
    onPageChange(page) {
      this.table.paging.page = page
      this.$store.dispatch('storageLocal/updateItem', {
        key: 'all-state-scores',
        subKey: 'paging.page',
        value: this.table.paging.page
      })
    },
    onPageSize(size) {
      this.table.paging.size = size
      this.$store.dispatch('storageLocal/updateItem', {
        key: 'all-state-scores',
        subKey: 'paging.size',
        value: this.table.paging.size
      })
    },

    /** Sorting **/
    persistSortPlan() {
      if(this.$store.getters['storageLocal/isRouteLocalEnabled']('all-state-scores')) {
        this.$store.dispatch('storageLocal/updateItem', {
          key: 'all-state-scores',
          subKey: 'sorting',
          value: { plan: this.table.sorting.plan },
        })

        console.log('Persisted Sort Plan:', this.table.sorting.plan)
      }
    },
    sanitizeSortPlan(plan) {
      const fields = this.table.sorting.fields || []
      const validKeys = fields.map(f => f && f.key).filter(Boolean)

      const cleaned = (plan || [])
          .filter(s => s && s.key && validKeys.includes(s.key))
          .map(s => ({ key: s.key, dir: s.dir === 'desc' ? 'desc' : 'asc' }))

      // Always guarantee at least 1 sort level
      if (!cleaned.length) {
        return [
          { key: 'student.name.last', dir: 'asc' },
          { key: 'student.name.first', dir: 'asc' },
          { key: 'ranking.local', dir: 'asc' },
          { key: 'ranking.score', dir: 'desc' },
        ]
      }

      return cleaned
    },
    getSortFieldMeta(key) {
      return this.table.sorting.fields.find(f => f.key === key) || { type: 'string' }
    },
    getSortValue(row, key, type) {
      const v = _.get(row, key)

      // treat null/undefined as "empty" so it sorts consistently
      if (v === null || v === undefined) return null

      if (type === 'number') {
        const n = Number(v)
        return Number.isNaN(n) ? null : n
      }

      if (type === 'date') {
        const t = new Date(v).getTime()
        return Number.isNaN(t) ? null : t
      }

      // string default
      return _.toString(v).trim().toLowerCase()
    },
    compareNulls(a, b, dir) {
      // put nulls last in asc, first in desc (feel free to flip)
      if (a === null && b === null) return 0
      if (a === null) return dir === 'asc' ? 1 : -1
      if (b === null) return dir === 'asc' ? -1 : 1
      return null // means "both non-null"
    },
    compareByPlan(aRow, bRow) {
      const plan = this.table.sorting.plan || []

      for (let i = 0; i < plan.length; i++) {
        const step = plan[i]

        if (step && step.key) {
          const meta = this.getSortFieldMeta(step.key)
          const dir = step.dir === 'desc' ? 'desc' : 'asc'
          const aVal = this.getSortValue(aRow, step.key, meta.type)
          const bVal = this.getSortValue(bRow, step.key, meta.type)

          const nullCmp = this.compareNulls(aVal, bVal, dir)

          if (nullCmp !== null) {
            // if one is null and the other isn't, we're done
            if (nullCmp !== 0) return nullCmp
            // else (both null) -> just try next sort level (no continue needed)
          } else {
            let cmp = 0

            if (meta.type === 'number' || meta.type === 'date') {
              cmp = aVal - bVal
            } else {
              cmp = _.toString(aVal).localeCompare(_.toString(bVal), 'en', { numeric: true })
            }

            if (cmp !== 0) return dir === 'asc' ? cmp : -cmp
            // else equal -> try next sort level
          }
        }
      }
      // stable-ish fallback: id
      return _.toString(aRow.id).localeCompare(_.toString(bRow.id), 'en', { numeric: true })
    },
  },
}
</script>

<style lang="scss">
.dropdown-menu-lg {
  min-width: 24rem;
}
.ps-changes {
  max-height: 300px;
}
.ps--active-y.ps-changes {
  padding-right: 1.5rem
}
.ps-processed {
  max-height: 50vh;
}
.ps--active-y.ps-processed {
  padding-right: 1.5rem
}
.alert .list-group .list-group-item:hover {
  background-color: #fff;
}

.table-row-action {
  width: 33px;
  text-align: center;
  padding-left: 1.5rem!important;
  padding-right: 1.5rem!important;
}

.form-group.is-invalid .v-select .vs__dropdown-toggle {
  border-color: #ea5455;
}

.form-group.is-valid .v-select .vs__dropdown-toggle {
  border-color: #28c76f;
}

.btn-transparent.dropdown-toggle::after {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-down'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
}

.sort-level {
  cursor: grab;
}
.sort-level.is-dragging {
  opacity: 0.6;
}
.sort-drag-handle {
  cursor: grab;
}
.sort-drag-handle:active {
  cursor: grabbing;
}
.dropdown-menu-sort {
  min-width: 28rem;     /* pick your size */
  width: 28rem;         /* forces it (helps if content is narrow) */
  padding: .75rem;      /* makes it feel like a card */
}
</style>

<style lang="scss">
@media print {
  #table-filters,
  #table-header,
  #table-footer,
  .table-row-options {
    display: none;
  }

  .table.b-table > thead > tr > [aria-sort] {
    background-image: none;
  }

  .form-control {
    border: 0!important;
  }
}
</style>

<style lang="scss" scoped>
.item-view-radio-group ::v-deep {
  .btn {
    display: flex;
    align-items: center;
  }
}

.table.b-table > thead > tr > th {
  background-color: red;
}

.b-table::v-deep {
  th {
    border: 0 !important;
  }
}

#search-apps.form-control-md {
  padding: 1.385rem 1.5rem
}

#applications-list .ps {
  min-height: 50vh;
  height: 50vh;
}
.prepend-group-text {
  min-width: 7.75rem;
}
</style>

<style lang="scss" scoped>
::v-deep {
  .col-student {
    min-width: 150px;
    width: auto;
    vertical-align: middle !important;
  }
  .col-ranking-local {
    min-width: 150px;
    width: auto;
    max-width: 200px;
    vertical-align: middle !important;
  }
  .col-ranking-score {
    min-width: 150px;
    width: auto;
    max-width: 200px;
    vertical-align: middle !important;
  }
  .col-row-action {
    width: 20px;
    vertical-align: middle !important;
    padding: 0.5rem!important;
  }

  @media (max-width: 1439px) {
    .media-aside {
      margin-right:0!important;
      display: none!important;
    }
    .b-avatar {
      display: none!important;
    }
    .student-school {
      display: none!important;
    }
    .festival-prefix {
      display: none!important;
    }
  }


  @media print {
    .card .card-header {
      padding-top: 0!important;
    }
    .form-control {
      border: 0!important;
    }
    .vs__dropdown-toggle {
      border: 0!important;
    }
    .vs__actions {
      display: none!important;
    }
    .media-aside {
      margin-right: 0;
    }
    .b-avatar {
      display: none;
    }
    .col-row-action {
      display: none;
    }
  }
}

.v-select::v-deep {
  .v-select .dropdown-menu {
    width: auto;
    max-width: 100%; /* Change this to the maximum width you want */
  }
}
</style>
